import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Icon from '@material-ui/core/Icon';
import Launch from '@material-ui/icons/Launch';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';

import ServiceIcon from 'components/ServiceIcon/ServiceIcon';
import SERVICES_WITH_USERS from 'constants/servicesWithUsers';
import SERVICES_WITH_DATABASES from 'constants/servicesWithDatabases';
import ORSpaceUsage from 'components/ORSpaceUsage/ORSpaceUsageContainer';
import { bytesToGB } from 'services/conversions';
import roleDisabled from 'services/roleDisabled/index';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import ORTypography from 'components/ORTypography/ORTypography';
import FeaturesList from 'components/MissionCtrl/InstancesPage/FeaturesList/FeaturesList';
import AclInstanceButtonItem from './AclInstanceButtonItem/AclInstanceButtonItem';
import UserInstanceButtonItem from './UserInstanceButtonItem/UserInstanceButtonItem';
import DatabaseInstanceButtonItem from './DatabaseInstanceButtonItem/DatabaseInstanceButtonItem';
import RegionInstanceButtonItem from './RegionInstanceButtonItem/RegionInstanceButtonItem';

const propTypes = {
  acls: PropTypes.instanceOf(Array).isRequired,
  id: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  statusDisplay: PropTypes.string.isRequired,
  dtCreated: PropTypes.string.isRequired,
  dtModified: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  shortId: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  provider: PropTypes.string.isRequired,
  classes: PropTypes.shape({}).isRequired,
  serviceMaturity: PropTypes.number.isRequired,
  hosts: PropTypes.instanceOf(Array).isRequired,
  spaceUsage: PropTypes.shape({
    sampleTime: PropTypes.number,
    spaceTotal: PropTypes.number,
    spaceAvailable: PropTypes.number,
    spaceUsed: PropTypes.number,
    nodes: PropTypes.arrayOf(PropTypes.shape({ spaceTotal: PropTypes.number })),
  }),
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  getTotalSpace: PropTypes.func.isRequired,
  getRedisMemoryCapacity: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    multiInstanceId: PropTypes.string,
  }).isRequired,
  getUnusedCockroachDBRegions: PropTypes.func.isRequired,
  features: PropTypes.shape({
    addons: PropTypes.shape({}),
  }).isRequired,
};

const defaultProps = {
  spaceUsage: {},
};

export function UnstyledInstanceItem({
  acls,
  id,
  version,
  statusDisplay,
  dtCreated,
  dtModified,
  name,
  shortId,
  service,
  region,
  provider,
  classes,
  serviceMaturity,
  hosts,
  spaceUsage,
  userRoles,
  getTotalSpace,
  getRedisMemoryCapacity,
  metadata,
  getUnusedCockroachDBRegions,
  features,
}) {
  let statusColor;
  let statusTextColor;
  const serviceStatus = statusDisplay;
  if (statusDisplay === 'Ready') {
    statusColor = classes.ready;
    statusTextColor = classes.textReady;
  } else if (statusDisplay === 'Failed') {
    statusColor = classes.failed;
    statusTextColor = classes.textFailed;
  } else {
    statusColor = classes.building;
    statusTextColor = classes.textBuilding;
  }

  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];

  const allowedUserRolesInstanceDetail = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];

  const detailButton = (disabled = false) => (
    <Button
      variant="contained"
      hover-override="black"
      disabled={disabled}
      className={classes.moreDetailsButton}
    >
      view more details
    </Button>
  );

  const createInstanceDetailButton = (button, userAccess, allowedAccess) =>
    roleDisabled(userAccess, allowedAccess) ? (
      <Tooltip title="You do not have the required access for this resource. Contact your organization owner.">
        <span className={classes.detailLink}>{button(true)}</span>
      </Tooltip>
    ) : (
      <Link className={classes.detailLink} to={`/mission-ctrl/instances/${service}/${id}`}>
        {button()}
      </Link>
    );

  return (
    <ExpansionPanel
      classes={{
        root: classes.panel,
        expanded: classes.panelExpanded,
      }}
      TransitionProps={{ unmountOnExit: true }}
    >
      <ExpansionPanelSummary
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
          expanded: classes.sumamryExpanded,
          focused: classes.summaryFocused,
        }}
        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
        IconButtonProps={{
          classes: {
            root: classes.expandIconButton,
          },
        }}
      >
        <div className={statusColor} />
        <div className={classes.summary}>
          <div className={classes.flexContainer}>
            <ServiceIcon
              className={classes.serviceIcon}
              service={service}
              isAlpha={serviceMaturity === 1}
              status={serviceStatus}
              badgeClass={classes.badgeClass}
            />
            <div className={classes.instanceInfo}>
              <Typography className={classes.name} variant="h6">
                {name}
              </Typography>
              <Typography variant="caption" className={classes.productInfo}>
                {service}
              </Typography>
              {statusDisplay === 'Ready' &&
                (getTotalSpace(id) !== 0 || service === 'Redis') && ( // bypass redis host = redis_replica returning 0 for totalSize
                  <Typography variant="caption" className={classes.productInfo}>
                    {service !== 'Redis' ? getTotalSpace(id) : getRedisMemoryCapacity(id)}
                    GB
                    {service !== 'Redis' ? null : '\u00a0RAM'}
                  </Typography>
                )}
            </div>
          </div>
          <div className={classes.actionArea}>
            {[...SERVICES_WITH_DATABASES].includes(service) && (
              <DatabaseInstanceButtonItem
                key="databaseforminstancebtn"
                classes={{
                  popoutIconLabel: classes.popoutIconLabel,
                }}
                statusDisplay={statusDisplay}
                instanceId={id}
                service={service}
                disabled={statusDisplay !== 'Ready' || roleDisabled(userRoles, allowedUserRoles)}
              />
            )}
            {[...SERVICES_WITH_USERS].includes(service) && (
              <UserInstanceButtonItem
                key="userforminstancebtn"
                classes={{
                  popoutIconLabel: classes.popoutIconLabel,
                }}
                statusDisplay={statusDisplay}
                instanceService={service}
                instanceId={id}
                disabled={statusDisplay !== 'Ready' || roleDisabled(userRoles, allowedUserRoles)}
              />
            )}
            <AclInstanceButtonItem
              key="aclforminstancebtn"
              classes={{
                popoutIconLabel: classes.popoutIconLabel,
              }}
              statusDisplay={statusDisplay}
              instanceService={service}
              instanceId={id}
              newInstanceAcls={acls}
              disabled={statusDisplay !== 'Ready' || roleDisabled(userRoles, allowedUserRoles)}
            />
            {service === 'CockroachDB' && metadata.multiInstanceId && (
              <RegionInstanceButtonItem
                key="regionforminstancebtn"
                classes={{
                  popoutIconLabel: classes.popoutIconLabel,
                }}
                statusDisplay={statusDisplay}
                instanceId={id}
                metadata={metadata}
                provider={provider}
                region={region}
                name={name}
                version={version}
                service={service}
                disabled={
                  statusDisplay !== 'Ready' ||
                  roleDisabled(userRoles, allowedUserRoles) ||
                  getUnusedCockroachDBRegions(id).length === 0
                }
                regionsUnavailable={getUnusedCockroachDBRegions(id).length === 0}
              />
            )}
          </div>
        </div>
        {statusDisplay !== 'Ready' && statusDisplay !== 'Failed' ? (
          <LinearProgress
            classes={{
              root: classes.progress,
              colorPrimary: classes.progressPrimary,
              bar: classes.progressSecondary,
            }}
          />
        ) : null}
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.details}>
        <div className={classes.charContainer}>
          <ORSpaceUsage
            userRoles={userRoles}
            instanceId={id}
            instanceIsReady={statusDisplay.toLowerCase() === 'ready'}
          />
        </div>
        <div className={classes.detailsListContainer}>
          <div className={classes.detailsListInfoContainer}>
            <Icon className={classes.detailsListIcon}>language_24</Icon>
            <div className={classes.detailsListLocationContainer}>
              <ORTypography className={classes.detailsListLocation}>{region}</ORTypography>
              <ORTypography className={classes.detailsListSize}>
                {spaceUsage &&
                  service !== 'Redis' &&
                  spaceUsage.nodes &&
                  spaceUsage.nodes.length > 0 &&
                  spaceUsage.nodes &&
                  bytesToGB(spaceUsage.nodes[0].spaceTotal).toFixed(0)}
                {service === 'Redis' && statusDisplay === 'Ready' && getRedisMemoryCapacity(id)}
                GB
                {service !== 'Redis' ? '\u00a0' : '\u00a0RAM\u00a0'}
                <span className={classes.detailsListSizeParenthesis}>&#40;per data node&#41;</span>
              </ORTypography>
            </div>
          </div>
          <ul className={classes.detailsList}>
            <li>
              <Typography variant="subtitle1">
                Engine:&nbsp;
                {service}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1" className={statusTextColor}>
                <span className={classes.detailsListReadyState}>State:&nbsp;</span>
                {statusDisplay === 'Ready' ? 'Active' : statusDisplay}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                Version:&nbsp;
                {version}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                ID:&nbsp;
                {id}
              </Typography>
            </li>
            {service === 'MongoDB' ? (
              <li>
                <Typography variant="subtitle1">
                  Short ID:&nbsp;
                  {shortId}
                </Typography>
              </li>
            ) : null}
            <li>
              <Typography variant="subtitle1">
                Created:&nbsp;
                {moment(dtCreated).format('dddd, MMMM Do YYYY, h:mm:ss a')}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                Modified:&nbsp;
                {moment(dtModified).format('dddd, MMMM Do YYYY, h:mm:ss a')}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                Cloud Provider:&nbsp;
                {provider}
              </Typography>
            </li>
            <FeaturesList features={features} />
          </ul>
        </div>
        <div className={classes.detailContainer}>
          {hosts.map((host, index) =>
            (service === 'Elasticsearch' &&
              host.connectionUri &&
              host.role !== 'elasticsearch' &&
              statusDisplay === 'Ready') ||
            host.role === 'cockroachdb_admin' ? (
              <a
                key={index.toString()}
                className={classes.featureDetailLink}
                href={host.connectionUri}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="contained"
                  hover-override={host.role}
                  className={classes[`${host.role}Button`]}
                >
                  <div className={classes.spacer} />
                  {host.role.split('_').join(' ')}
                  <Launch className={classes.featureButtonIcon} />
                </Button>
              </a>
            ) : null,
          )}
          {createInstanceDetailButton(detailButton, userRoles, allowedUserRolesInstanceDetail)}
        </div>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
}

UnstyledInstanceItem.propTypes = propTypes;
UnstyledInstanceItem.defaultProps = defaultProps;

const styles = (theme) => ({
  panel: {
    '&:first-child': {
      marginTop: `${theme.spacing(3)}px !important`,
    },
    '&:last-child': {
      marginBottom: '20rem',
    },
    marginTop: `${theme.spacing(1)}px`,
    marginBottom: 0,
  },
  panelExpanded: {
    marginTop: `${theme.spacing(1)}px !important`,
    marginBottom: '0 !important',
  },
  summaryRoot: {
    paddingLeft: 0,

    [theme.breakpoints.down('md')]: {
      maxHeight: 'unset',
    },
  },
  summaryContent: {
    display: 'flex',
    margin: '0 !important',
    '&$expanded': {
      margin: 0,
    },
    '& > :last-child': {
      paddingRight: '0px',
    },
  },
  summaryExpanded: {
    margin: 0,
  },
  summaryFocused: {
    backgroundColor: 'unset !important',
  },
  badgeClass: {
    top: theme.spacing(0.5),
    right: theme.spacing(2.5),

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  name: {
    whiteSpace: 'pre-wrap' /* css-3 */,
    whiteSpace: '-moz-pre-wrap' /* Mozilla, since 1999 */, // eslint-disable-line no-dupe-keys
    whiteSpace: '-pre-wrap' /* Opera 4-6 */, // eslint-disable-line no-dupe-keys
    whiteSpace: '-o-pre-wrap' /* Opera 7 */, // eslint-disable-line no-dupe-keys
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    wordBreak: 'break-all' /* Check this on Safari */,
    fontFamily: '"Libre Medium", sans-serif',
  },
  actionArea: {
    display: 'flex',
    flex: '1 0 30%',
    alignSelf: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
      justifyContent: 'flex-start',
      alignSelf: 'start',
      flex: '1 0 100%',
    },
  },
  details: {
    borderTop: ['1px', 'solid', theme.customColors.lightGrey].join(' '),
    justifyContent: 'space-between',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: '0px 16px',
    gridTemplateAreas: `
      'chart list'
      'chart buttons'
    `,
    padding: theme.spacing(1),

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '2fr 1fr',
    },

    [theme.breakpoints.down('sm')]: {
      gridTemplateAreas: `
      'list'
      'chart'
      'buttons'
    `,
      gridTemplateColumns: '1fr',
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
      '& ul': {
        margin: 0,
        paddingLeft: theme.spacing(2.5),
      },
    },
  },
  charContainer: {
    gridArea: 'chart',
  },
  detailsListContainer: {
    gridArea: 'list',
    justifySelf: 'center',
    [theme.breakpoints.down('md')]: {
      justifySelf: 'flex-start',
    },
  },
  detailsListInfoContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  detailsListLocationContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  detailsList: {
    gridArea: 'list',
    justifySelf: 'center',
    '& li': {
      paddingLeft: theme.spacing(2),
      '& h6': {
        fontFamily: '"Libre Franklin Regular", sans-serif',
        fontSize: '14px',
        lineHeight: '21px',
      },
    },
  },
  detailsListIcon: {
    gridArea: 'list',
    justifySelf: 'flex-start',
    marginLeft: theme.spacing(2),
    height: theme.spacing(3),
    width: theme.spacing(3),
    color: theme.customColors.mediumGrey,
  },
  detailsListLocation: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    paddingLeft: theme.spacing(2),
  },
  detailsListSize: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    paddingLeft: theme.spacing(2),
  },
  detailsListSizeParenthesis: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    color: theme.customColors.mediumGrey,
  },
  detailsListReadyState: {
    color: theme.customColors.black,
  },
  summary: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px 0`,
      maxHeight: 'unset',
    },
  },
  flexContainer: {
    display: 'flex',
    flexFlow: 'row',
    flex: '1 1 70%',
    width: '100%',
    minWidth: '50%',

    [theme.breakpoints.up('md')]: {
      alignSelf: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column nowrap',
    },
  },
  detailContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexFlow: 'column wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gridArea: 'buttons',

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: theme.spacing(1.25),
    },
  },
  serviceIcon: {
    height: '40px',
    width: '45px',
    display: 'inline-block',
    margin: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(1)}px ${theme.spacing(
      1,
    )}px`,
    paddingTop: theme.spacing(0.5),

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  instanceInfo: {
    flex: '1 1 50%',

    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(1),
    },
  },
  productInfo: {
    lineHeight: '1.1rem',
    display: 'block',
  },
  progress: {
    position: 'absolute',
    clear: 'both',
    left: 0,
    bottom: 0,
    width: '100%',
  },
  progressPrimary: {
    backgroundColor: theme.customColors.khaki,
  },
  progressSecondary: {
    backgroundColor: theme.customColors.burntYellow,
  },
  textReady: {
    color: theme.customColors.teal,
  },
  textFailed: {
    color: theme.customColors.magenta,
  },
  textBuilding: {
    color: theme.customColors.burntYellow,
  },
  ready: {
    minHeight: theme.spacing(10),
    width: theme.spacing(0.75),
    display: 'inline-block',
    backgroundColor: theme.customColors.orange,
  },
  failed: {
    minHeight: theme.spacing(10),
    width: theme.spacing(0.75),
    display: 'inline-block',
    backgroundColor: theme.customColors.orange,
  },
  building: {
    minHeight: theme.spacing(10),
    width: theme.spacing(0.75),
    display: 'inline-block',
    backgroundColor: theme.customColors.orange,
  },
  moreDetailsButton: {
    color: theme.customColors.black,
    width: '100%',
    padding: theme.spacing(1),
    fontWeight: 400,
  },
  detailLink: {
    textDecoration: 'none',
    width: '100%',
    marginTop: theme.spacing(1),
  },
  expandIcon: {
    color: theme.customColors.white,
  },
  expandIconButton: {
    borderRadius: '4px',
    padding: '6px',
    backgroundColor: theme.customColors.black,
    '&:hover:not($disabled)': {
      backgroundColor: theme.customColors.black,
    },
    boxShadow:
      '0 0 3px 1px rgba(0,0,0,0.2), 0 0 2px 01px rgba(0,0,0,0.14), 0 0 2px 1px rgba(0,0,0,0.12)',
    marginBottom: theme.spacing(2),
  },
  popoutIconLabel: {
    paddingTop: '0.75em',
    width: '100%',
    textTransform: 'none',
    fontSize: '.785714rem',

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  featureButtonIcon: {},
  featureDetailLink: {
    textDecoration: 'none',
    width: '100%',
  },
  kibanaButton: {
    gridArea: 'kibana',
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: theme.customColors.peach,
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  cerebroButton: {
    gridArea: 'cerebro',
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: '#7cbbbe',
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  cockroachdb_adminButton: {
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: '#7cbbbe',
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  spacer: {
    width: '25px',
  },
});

export default withStyles(styles)(UnstyledInstanceItem);
